import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import StatCard from '../components/StatCard';
import Pill from '../components/Pill';
import { fmt } from '../utils/format';

// ── Stage colour map ──────────────────────────────────────────────────────────
const stageMap = {
  'Dispatched':                { color: 'green', label: 'Dispatched' },
  'Fully Picked - Hold':       { color: 'gold',  label: 'Picked - Hold' },
  'Problem Encountered':       { color: 'gold',  label: 'Problem' },
  'To Call Customer':          { color: 'gold',  label: 'Call Customer' },
  'Fraud Warning':             { color: 'red',   label: 'Fraud Warning' },
  'Awaiting Payment':          { color: 'gold',  label: 'Awaiting Payment' },
  'Payment Received':          { color: 'gold',  label: 'Payment Received' },
  'Ready to Collect':          { color: 'blue',  label: 'Ready to Collect' },
  'New':                       { color: 'blue',  label: 'New' },
  'Warehouse Transfer':        { color: 'blue',  label: 'Warehouse Transfer' },
  'HC - Back Order':           { color: 'muted', label: 'Back Order' },
  'Awaiting Traxxas Shipment': { color: 'muted', label: 'Awaiting Shipment' },
  'Awaiting Proxxon Shipment': { color: 'muted', label: 'Awaiting Shipment' },
  'Repair':                    { color: 'muted', label: 'Repair' },
};

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
};

// ── Single SO block ───────────────────────────────────────────────────────────
const SOBlock = ({ order }) => {
  const [ignoredIds, setIgnoredIds] = useState(() => {
    const pre = new Set();
    for (const item of order.items) {
      if (item.link_status === 'ignored') pre.add(item.link_id);
    }
    return pre;
  });
  const [actioning, setActioning] = useState(null);
  const [notes, setNotes]         = useState(() => {
    const n = {};
    for (const item of order.items) { if (item.link_id && item.notes) n[item.link_id] = item.notes; }
    return n;
  });
  const [savingNote, setSavingNote] = useState(null);

  const stageInfo   = stageMap[order.stage] || { color: 'muted', label: order.stage || 'Unknown' };
  const customer    = [order.first_name, order.last_name].filter(Boolean).join(' ') || '—';
  const createdDate = order.created_date
    ? new Date(order.created_date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';
  const days   = daysSince(order.created_date);
  const isVoid = order.status === 'VOID';

  const handleIgnore = async (e, linkId) => {
    e.stopPropagation();
    if (actioning) return;
    setActioning(linkId);
    const { error } = await supabase
      .from('smart_buyer_fulfilment_links')
      .update({ status: 'ignored' })
      .eq('id', linkId);
    if (!error) setIgnoredIds(prev => new Set([...prev, linkId]));
    else alert(`Failed: ${error.message}`);
    setActioning(null);
  };

  const handleNoteBlur = async (linkId) => {
    const note = notes[linkId] ?? '';
    setSavingNote(linkId);
    await supabase
      .from('smart_buyer_fulfilment_links')
      .update({ notes: note || null })
      .eq('id', linkId);
    setSavingNote(null);
  };

  return (
    <div className="so-group">
      <div className="so-group__header">
        <span className="code-badge">{order.order_ref || order.order_id}</span>
        <span className="so-group__customer">{customer}</span>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>{createdDate}</span>
        <span className={`pill pill--${stageInfo.color}`}>{stageInfo.label}</span>
        {isVoid && (
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--red, #ef4444)', marginLeft: 6 }}>
            VOID
          </span>
        )}
        {days !== null && (
          <span style={{ fontSize: 11, color: days > 7 ? 'var(--red-light, #f87171)' : 'var(--muted)', marginLeft: 'auto' }}>
            {days}d old
          </span>
        )}
      </div>

      <div className="so-group__items">
        {order.items.map((item, i) => {
          const hasLink    = !!item.link_id;
          const isCancelled = hasLink && ignoredIds.has(item.link_id);
          const isBusy     = actioning === item.link_id;
          const fullyMoved = Number(item.qty_moved) >= Number(item.qty_ordered);

          if (isCancelled) return null;

          return (
            <div key={i} className="so-item">
              <span className="code-badge code-badge--sm">{item.item_code}</span>
              <span className="so-item__name">{item.item_name}</span>
              <span className="so-item__qty">
                ordered <strong>{fmt(item.qty_ordered)}</strong>
                &nbsp;·&nbsp;
                moved <strong className={fullyMoved ? 'qty-ok' : 'qty-short'}>{fmt(item.qty_moved)}</strong>
                {item.po_number && (
                  <span style={{ color: 'var(--muted)', marginLeft: 4 }}>
                    (Ordered on PO: <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--code)' }}>{item.po_number}</span>)
                  </span>
                )}
                {' '}
                <span className={`so-item__dot ${fullyMoved ? 'so-item__dot--green' : 'so-item__dot--red'}`}>●</span>
              </span>

              {hasLink && (
                <button
                  className="ignore-btn"
                  onClick={(e) => handleIgnore(e, item.link_id)}
                  disabled={isBusy}
                  title="Mark as ignored"
                >
                  {isBusy ? '…' : '✕ Ignore'}
                </button>
              )}

              {hasLink && (
                <input
                  className={`note-input ${savingNote === item.link_id ? 'note-input--saving' : ''}`}
                  type="text"
                  placeholder="Add note…"
                  value={notes[item.link_id] || ''}
                  onChange={(e) => setNotes(prev => ({ ...prev, [item.link_id]: e.target.value }))}
                  onBlur={() => handleNoteBlur(item.link_id)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const OpenPOTracker = () => {
  const navigate = useNavigate();
  const [orders, setOrders]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const load = useCallback(async () => {
    setLoading(true);

    const { data, error } = await supabase.rpc('get_open_po_worklist');

    if (error) {
      console.error('get_open_po_worklist error:', error);
      setOrders([]);
      setLoading(false);
      return;
    }

    const rows = data || [];

    // Group flat rows into orders with items arrays
    const orderMap = new Map();
    for (const row of rows) {
      if (!orderMap.has(row.order_id)) {
        orderMap.set(row.order_id, {
          order_id:     row.order_id,
          order_ref:    row.order_ref,
          created_date: row.created_date,
          stage:        row.stage,
          status:       row.status,
          first_name:   row.first_name || '',
          last_name:    row.last_name  || '',
          items:        [],
        });
      }
      orderMap.get(row.order_id).items.push({
        item_code:   row.item_code,
        item_name:   row.item_name || row.item_code,
        qty_ordered: row.qty_ordered,
        qty_moved:   row.qty_moved ?? 0,
        link_id:     row.link_id,
        link_status: row.link_status,
        notes:       row.notes || '',
        po_number:   row.po_number,
      });
    }

    setOrders([...orderMap.values()]);
    setLoading(false);
    setLastRefresh(new Date());
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalItems = orders.reduce((s, o) => s + o.items.length, 0);

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/frontline-tools')}>← Frontline Tools</button>
        <span className="page-nav__title">Open PO Worklist</span>
        <span className="app-header__refresh">Last refresh: {lastRefresh.toLocaleTimeString('en-NZ')}</span>
        <button className="app-header__btn" onClick={load}>↻ Refresh</button>
      </div>

      <div className="stats-row">
        <StatCard label="Orders Pending"        value={orders.length} accent="blue" />
        <StatCard label="Unfulfilled Line Items" value={totalItems}    accent="gold" />
      </div>

      <section className="section">
        <SectionHeader
          title="Sales Orders — Open PO, Unfulfilled"
          count={orders.length}
          onRefresh={load}
          loading={loading}
        />
        <p className="section__desc">
          Sales orders older than 3 days, not yet dispatched, with unfulfilled items tied to an open purchase order. Oldest first. Use Ignore to dismiss a line once actioned.
        </p>

        {loading && <div className="loading-row">Loading…</div>}
        {!loading && !orders.length && (
          <div className="empty-row">✓ No qualifying orders</div>
        )}
        {!loading && orders.map(order => (
          <SOBlock key={order.order_id} order={order} />
        ))}
      </section>
    </main>
  );
};

export default OpenPOTracker;
